import type { ModDef } from './mods';
import type { SecondaryIndex } from './secondary';
import { BASE_STATS, computeStats, dps, STAT_CAPS, type Modifier, type StatId } from './stats';

/* ═══════════════════════════════════════════════════════════════════════════
   Les FORMES que la simulation sait exécuter.

   ⚠️ Décision d'architecture, et c'est celle qui décide si la machine à contenu
   tiendra : ces types vivent dans src/sim/, pas dans src/content/.

   La simulation exécute des formes ; le contenu fournit des données. Si la
   simulation importait le contenu, la flèche de dépendance s'inverserait
   (platform ← sim ← content ← view) et l'on ne pourrait plus rejouer une partie
   headless sans embarquer tout le catalogue. Ici, `World` reçoit un index de
   définitions à sa construction : la simulation ne connaît que la forme, jamais
   les fichiers.

   Conséquence pratique et vérifiable : ajouter un ennemi = créer UN fichier
   dans src/content/enemies/, sans toucher à une seule ligne de moteur.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Trajectoires connues du moteur. En ajouter une est une modification de
 *  MOTEUR, délibérément — c'est la frontière entre « nouveau contenu » et
 *  « nouvelle capacité ». */
export const Motion = {
  /** Descend tout droit. */
  Linear: 0,
  /** Descend en ondulant. */
  Sine: 1,
  /** Descend jusqu'à `holdY` puis se poste et oscille latéralement. */
  Hover: 2,
  /** Traverse latéralement à hauteur constante. */
  Strafe: 3,
  /** Descend LENTEMENT jusqu'à `holdY`, s'y immobilise, puis PLONGE à pleine
   *  vitesse une fois `hold` secondes écoulées depuis son apparition.
   *
   *  Le contraste lent → rapide est toute la leçon : la pause est le télégraphe
   *  du piqué. C'est aussi la seule menace corporelle du bestiaire, donc le seul
   *  contre au camping — un joueur posé dans un coin doit être délogé par
   *  quelque chose qui vient le chercher, pas par un projectile de plus.
   *
   *  ⚠️ Le piqué exige les DEUX conditions (arrivé à `holdY` ET `hold` écoulé).
   *  Le déclencheur purement temporel a été écarté : les apparitions sont
   *  dispersées verticalement (`PF_HH + 60 + rng(0..90)`), et un piqué déclenché
   *  à l'horloge partirait donc d'une altitude différente à chaque fois. Le
   *  temps de réaction laissé au joueur varierait de 12 % sans qu'aucune règle
   *  visible ne l'explique. Ici l'altitude de départ est EXACTEMENT `holdY`,
   *  toujours, et seule la durée de la pause absorbe la dispersion. */
  Dive: 4,
  /** Descend jusqu'à `holdY` puis se fige — définitivement, et sans la moindre
   *  oscillation.
   *
   *  C'est ce qui le distingue de `Hover`, dont le balancement latéral suffit à
   *  le faire lire comme un être vivant. Un ennemi rigoureusement immobile
   *  cesse d'être perçu comme un adversaire et devient du DÉCOR : le joueur
   *  arrête de le suivre des yeux et compose avec lui comme avec un obstacle
   *  fixe. C'est le seul moyen d'obtenir du terrain tactique dans un jeu où
   *  rien n'est collisionnable. */
  Anchor: 5,
} as const;

/** Patterns de tir connus du moteur. */
export const Pattern = {
  /** Ne tire pas. */
  None: 0,
  /** Visé sur le joueur, éventuellement en éventail. */
  Aimed: 1,
  /** Éventail STATIQUE, indépendant du joueur : il interdit une zone au lieu
   *  de poursuivre. C'est le contrepoint indispensable au tir visé — sans lui,
   *  toutes les menaces suivent le joueur et le terrain n'a plus de géographie. */
  Fan: 2,
  /** Anneau complet, avec rotation par volée pour que deux anneaux successifs
   *  ne se superposent jamais. */
  Ring: 3,
  /** Ligne horizontale de projectiles, percée d'un TROU qui glisse d'une volée
   *  à la suivante.
   *
   *  C'est le seul pattern qui IMPOSE une trajectoire au lieu d'interdire une
   *  zone, et le glissement du trou est ce qui le rend supportable : trois murs
   *  consécutifs déplacent le trou dans le MÊME SENS avant d'inverser, si bien
   *  que le joueur accompagne le mouvement au lieu de s'arrêter à chaque mur.
   *  Un trou qui sauterait au hasard produirait exactement l'inverse — un
   *  arrêt, une relecture, un redémarrage — et casserait le flux à chaque
   *  volée. */
  Wall: 4,

  /* ── Patterns à CONDUITE (exécutés par enemyBulletSystem) ───────────────────
     Les quatre premiers décrivent la GÉOMÉTRIE d'une volée ; ceux-ci décrivent
     ce que fait un projectile UNE FOIS PARTI. C'est la même frontière que côté
     joueur (`PbMode` dans BulletPool) : ajouter une conduite est une extension
     du moteur, pas du contenu, et elle se paie en état haché (voir Snapshot).

     ⚠️ Chacun MARQUE ses projectiles via `w.eb.behave(...)` à l'émission ; c'est
     `enemyBulletSystem`, avant l'intégration, qui les pilote. Un pattern à
     conduite dont personne ne lit la marque produit des balles balistiques
     ordinaires — panne silencieuse, jamais une erreur. */

  /** Missile GUIDÉ : lancé droit vers le bas, il vire ensuite vers le joueur,
   *  d'un virage BORNÉ par tick. Le virage n'est pas visé — un tir droit manque
   *  un joueur décalé, c'est le guidage qui rattrape l'erreur finale, et sa
   *  borne haute est ce qui laisse l'esquive possible (voir enemyBulletSystem). */
  Homing: 5,
  /** Anneau dont chaque projectile COURBE à vitesse constante, indépendamment
   *  du joueur : la figure radiale devient une spirale. Réutilise la rotation
   *  par volée de l'anneau pour que deux pulsations ne se superposent pas. */
  Spiral: 6,
  /** Faisceau PERSISTANT : une colonne de segments ancrés sous l'émetteur et
   *  repositionnés chaque tick. Il ne vole pas ; il balaie quand l'émetteur se
   *  déplace. L'équivalent ennemi du Rayon du joueur. */
  Beam: 7,

  /** RAFALE : une salve de projectiles visés tirés en SÉQUENCE rapide, puis une
   *  longue pause — une mitrailleuse, pas une gerbe simultanée.
   *
   *  C'est une forme temporelle, non géométrique : les autres patterns crachent
   *  `count` balles dans le même tick, celui-ci les étale sur `count` tirs
   *  espacés de `burstGap`. La leçon change avec le rythme : une gerbe simultanée
   *  s'esquive d'un pas de côté unique, une rafale suivie oblige à tenir un
   *  déplacement pendant toute la salve.
   *
   *  ⚠️ La densité est bornée par construction : `count` petites balles toutes
   *  les `every` secondes (une salve par cycle), jamais un flot continu. Une
   *  rafale ne doit pas à elle seule saturer l'écran (plafond 90/130/170 par
   *  monde) — voir le réglage du Mitrailleur. */
  Burst: 8,
} as const;

export interface MotionDef {
  readonly kind: number;
  /** u/s */
  readonly speed: number;
  /** Amplitude latérale (Sine, Hover). */
  readonly amplitude: number;
  /** Fréquence de l'oscillation, en rad/s. */
  readonly frequency: number;
  /** Ordonnée de stationnement (Hover, Dive, Anchor). */
  readonly holdY: number;
  /** Instant du piqué, en secondes depuis l'APPARITION (Dive).
   *
   *  ⚠️ Depuis l'apparition, et non depuis l'arrivée à `holdY` : compter à
   *  partir de l'arrivée demanderait un chronomètre par entité, donc un champ
   *  de pool de plus et une entrée de plus dans l'empreinte de replay, pour un
   *  gain nul. La contrepartie assumée est que la PAUSE varie avec la
   *  dispersion des apparitions (±0,5 s ici) ; ce qui ne varie pas, c'est
   *  l'altitude d'où part le piqué — et c'est la seule des deux que le joueur
   *  doit pouvoir anticiper.
   *
   *  Régler `hold` trop court n'est pas dangereux : le piqué attend malgré tout
   *  d'être arrivé à `holdY`. On ne peut donc pas produire par erreur un Dard
   *  qui plonge depuis le hors-champ. */
  readonly hold?: number | undefined;
}

export interface EmitterDef {
  readonly pattern: number;
  /** Période entre deux volées, en secondes. */
  readonly every: number;
  /** Projectiles par volée. */
  readonly count: number;
  /** Ouverture totale de l'éventail, en degrés. */
  readonly spreadDeg: number;
  /** u/s */
  readonly speed: number;
  /** Rotation appliquée à chaque volée, en degrés (Ring). */
  readonly stepDeg: number;
  /** Délai avant la première volée, en secondes. */
  readonly firstDelay: number;
  /** Famille visuelle du projectile (palette réservée). */
  readonly family: number;

  /* ── Propres au mur (Pattern.Wall) ──────────────────────────────────────
     Deux champs neufs plutôt qu'une réinterprétation de `spreadDeg` et de
     `stepDeg`, qui auraient pourtant joué le bon rôle.

     La raison tient à leur SUFFIXE : un mur se mesure en unités, pas en
     degrés. Écrire `spreadDeg: 650` compilerait, produirait un mur correct, et
     deviendrait un éventail de 650° le jour où quelqu'un basculerait le pattern
     sur Fan pour essayer — sans un mot d'avertissement, puisque le champ aurait
     gardé son nom et son type. Un nom qui ment sur son unité est une panne
     différée. */

  /** Largeur totale du mur, en unités (Wall).
   *
   *  ⚠️ Elle est bornée par la règle du couloir unique (PLAN.md §6.5) : voir
   *  `Pattern.Wall` dans emitterSystem pour le calcul des flancs. */
  readonly width?: number | undefined;
  /** Glissement du trou d'une volée à la suivante, en unités (Wall).
   *
   *  Valeur DEMANDÉE : le moteur la quantifie sur le pas du mur, un trou entre
   *  deux emplacements n'ayant pas de sens. Le sens du glissement, lui, est
   *  respecté exactement — trois murs dans une direction, puis trois dans
   *  l'autre. */
  readonly slide?: number | undefined;

  /* ── Propres aux patterns à CONDUITE ────────────────────────────────────
     Champs neufs plutôt qu'une réinterprétation de `spreadDeg`/`stepDeg`, pour
     la même raison que `width`/`slide` : un nom qui ment sur son unité est une
     panne différée. Un virage se mesure en degrés PAR SECONDE, pas en degrés. */

  /** Vitesse de rotation d'un projectile guidé ou courbe, en degrés/seconde.
   *    · Homing → virage MAXIMAL vers le joueur par tick (borne de l'esquive) ;
   *    · Spiral → courbe CONSTANTE, indépendante du joueur (signe = sens). */
  readonly turnDeg?: number | undefined;
  /** Longueur du faisceau persistant vers le bas, en unités (Beam). Bornée par
   *  la règle du télégraphe : le bas du faisceau doit rester au-dessus de
   *  Y = −180 (aucun tir sous cette ligne sans télégraphe, PLAN.md §6.5). */
  readonly beamLen?: number | undefined;
  /** Durée de persistance du faisceau par volée, en secondes (Beam).
   *
   *  ⚠️ Doit rester STRICTEMENT sous `every` : deux faisceaux qui se
   *  chevauchent produiraient une double barrière qu'aucune trouée de temps ne
   *  vient jamais ouvrir, c'est-à-dire une zone morte permanente.
   *
   *  ⚠️ Avec `beamWarn`, c'est `beamWarn + beamLife` qui doit rester sous
   *  `every` : la portion basse ne devient létale QU'APRÈS l'avertissement, mais
   *  le segment vit les deux durées. */
  readonly beamLife?: number | undefined;
  /** Avertissement AVANT qu'un faisceau ne devienne létal SOUS la ligne des
   *  −180 u, en secondes (Beam pleine hauteur).
   *
   *  ⚠️ Anti-frustration §6.5 : aucun tir sous Y = −180 sans télégraphe d'au
   *  moins 1,4 s. Un faisceau qui reste au-dessus de cette ligne (Sabreur) n'en
   *  a pas besoin et laisse ce champ absent. Un faisceau PLEINE HAUTEUR, lui, le
   *  DOIT : ses segments bas naissent parqués à l'ancre (inoffensifs, en haut) et
   *  ne descendent dans la rangée du joueur qu'à l'expiration du délai — le haut
   *  du faisceau, resté au-dessus de −180 et donc toujours licite, sert
   *  lui-même de télégraphe visible de la colonne condamnée. */
  readonly beamWarn?: number | undefined;
  /** Intervalle entre deux tirs D'UNE MÊME rafale, en secondes (Burst).
   *
   *  Il est distinct d'`every`, qui est la période d'un CYCLE complet (une salve
   *  puis la longue pause). `burstGap` doit rester petit — c'est ce qui fait lire
   *  la salve comme une giclée et non comme des tirs isolés. */
  readonly burstGap?: number | undefined;
}

/** Échappatoire documentée (PLAN.md §3.3).
 *
 *  Le dogme « zéro if (enemy.type === x) dans le moteur » est une bonne ligne
 *  directrice et un absolu catastrophique : couvrir déclarativement tous les
 *  comportements revient à concevoir un mini-langage de script avec une
 *  ergonomie pire que du TypeScript. Coût estimé de l'entêtement : 3 à 5
 *  semaines.
 *
 *  Les fichiers de contenu ÉTANT déjà des .ts, on autorise un hook — soumis
 *  aux mêmes règles que src/sim/ : pur, sans allocation, sans DOM, sans Math
 *  transcendante. C'est un DERNIER RECOURS, pas le chemin par défaut : si plus
 *  de 30 % des définitions en utilisent, c'est le schéma qui est faux. */
export type EnemyUpdateHook = (
  slot: number,
  age: number,
  dt: number,
  ctx: EnemyHookContext,
) => void;

export interface EnemyHookContext {
  readonly playerX: number;
  readonly playerY: number;
  readonly x: Float32Array;
  readonly y: Float32Array;
}

/** Rang d'un ennemi. C'est une donnée de CONTENU, pas une déduction du moteur :
 *  la présentation s'en sert pour doser le gel et la secousse sans jamais avoir
 *  à connaître un identifiant d'ennemi en particulier. Ajouter un archétype ne
 *  demande donc de toucher ni au Director, ni au rendu. */
export const Rank = {
  /** Meurt en un coup, ne coûte aucune attention. ZÉRO gel — règle sacrée. */
  Popcorn: 0,
  Standard: 1,
  Heavy: 2,
} as const;

/** Une partie destructible rattachée à un porteur (tourelle, aile, bras).
 *
 *  Elle référence un `EnemyDef` ordinaire : une tourelle est un ennemi comme
 *  un autre, qui se trouve avoir un parent. Rien de spécifique n'est nécessaire
 *  côté moteur — c'est ce qui rend le système peu coûteux. */
export interface PartDef {
  readonly enemy: string;
  readonly offX: number;
  readonly offY: number;
}

/** Ponte / éclosion — la SEULE vraie capacité moteur neuve de cette passe.
 *
 *  Jusqu'ici seul `levelSystem` faisait apparaître des ennemis, à partir de la
 *  timeline. `brood` permet à un ennemi d'en ENGENDRER d'autres en cours de
 *  combat : un pondeur qui pose des œufs, un œuf qui éclôt en larve. C'est
 *  `broodSystem` (branché dans `step`, phase de peuplement) qui l'exécute, en
 *  passant par le pool comme n'importe quel spawn — donc télégraphe, collision,
 *  émetteurs et butin s'appliquent sans un cas particulier.
 *
 *  ⚠️ Deux bornes, et l'oubli de l'une ou l'autre casse le jeu :
 *    · `max` plafonne le nombre d'éclosions — un pondeur non borné sature le
 *      pool (256 slots) et le budget de frame ;
 *    · la CHAÎNE doit terminer sur une feuille. Œuf → larve où la larve n'a pas
 *      de `brood` : sans feuille, l'engendrement se propage sans fin.
 *  La saturation du pool est en outre gérée sans planter : un spawn refusé est
 *  simplement compté en overflow, la ponte n'incrémente pas son compteur et
 *  retente au cycle suivant. */
export interface BroodDef {
  /** Identifiant de l'ennemi engendré. */
  readonly enemy: string;
  /** Secondes entre deux pontes — et délai avant la première, compté depuis la
   *  fin du télégraphe du porteur (un œuf pondu ne doit pas éclore avant d'être
   *  apparu). */
  readonly every: number;
  /** Nombre MAXIMAL d'engendrés sur toute la vie du porteur. La borne dure. */
  readonly max: number;
  /** Décalage vertical du point d'apparition, en unités (négatif = sous le
   *  porteur, là où un œuf doit tomber). */
  readonly offY?: number | undefined;
  /** Le porteur DISPARAÎT après sa dernière ponte. C'est le comportement de
   *  l'ŒUF, qui éclôt une fois puis n'a plus de raison d'exister. Absent = il
   *  persiste, comme le PONDEUR qui reste en scène et continue de menacer. */
  readonly dieAfter?: boolean | undefined;
}

/** Une PHASE de combat — la notion de boss dans le moteur (PLAN.md §6.4).
 *
 *  Un ennemi ordinaire a UN comportement, immuable. Un boss ÉVOLUE au fil du
 *  combat : ses attaques changent quand ses PV descendent. `PhaseDef` décrit ce
 *  relais. La phase 0 est le comportement de base (`def.emitter`) ; chaque
 *  `PhaseDef` prend le relais quand la fraction de PV du CORPS passe sous son
 *  seuil. `phaseSystem` fait avancer la phase, re-télégraphie la bascule et la
 *  signale (`Ev.BossPhase`) ; `emitterSystem` lit alors l'émetteur de la phase
 *  courante au lieu de `def.emitter`.
 *
 *  Choix assumé : UN émetteur de cœur par phase (le `fireCd` du pool est unique).
 *  La simultanéité d'attaques reste portée par les PARTIES, qui ont chacune leur
 *  propre émetteur — un boss riche, c'est un cœur qui évolue pendant que ses
 *  tourelles tirent. */
export interface PhaseDef {
  /** Fraction de PV du CORPS (hors bouclier) EN DESSOUS de laquelle cette phase
   *  s'active. STRICTEMENT décroissante entre phases qui l'emploient (0,66 puis
   *  0,33…), vérifié à l'import. Optionnel : une phase peut se déclencher UNIQUEMENT
   *  sur la destruction de parties (`atPartsLost`). */
  readonly atHpFrac?: number | undefined;
  /** Nombre de PARTIES détruites À PARTIR duquel cette phase s'active — un boss qui
   *  RÉAGIT à son démantèlement, pas seulement à ses PV. `atHpFrac` OU `atPartsLost`
   *  déclenche (l'une suffit) ; une phase DOIT avoir au moins l'un des deux.
   *
   *  C'est ce qui débloque la vraie richesse d'un boss `shieldedByParts` : son cœur,
   *  intouchable tant que les parties vivent, peut quand même TIRER — et donc CHANGER
   *  d'attaque à mesure que le joueur démonte ses armes, avant même d'être exposé.
   *  STRICTEMENT croissant entre phases qui l'emploient, vérifié à l'import. */
  readonly atPartsLost?: number | undefined;
  /** Émetteur actif dans cette phase — remplace `def.emitter`. */
  readonly emitter: EmitterDef;
  /** Re-télégraphe à l'entrée de la phase, en secondes : le boss se fige et
   *  clignote, un signal que « ça change ». Absent = pas de re-télégraphe. */
  readonly telegraph?: number | undefined;
  /** Multiplicateur de CADENCE de cette phase (enrage) : > 1 accélère le tir.
   *  Absent = 1. N'accélère JAMAIS la vitesse des projectiles (§6.5, réaction). */
  readonly speedMul?: number | undefined;
  /** INVOCATION propre à cette phase : tant qu'elle est active, le boss engendre
   *  cet essaim (via `broodSystem`, comme un pondeur). Les compteurs de ponte sont
   *  remis à zéro à l'entrée de la phase → chaque phase invoque à neuf, avec son
   *  propre `max`. Absent = la phase n'invoque rien. */
  readonly brood?: BroodDef | undefined;
}

export interface EnemyDef {
  readonly id: string;
  readonly rank: number;
  readonly hp: number;
  /** Rayon VISUEL. La hurtbox en vaut 120 % (PLAN.md §6.2). */
  readonly visualRadius: number;
  readonly score: number;
  /** Durée du télégraphe, en secondes. */
  readonly telegraph: number;
  readonly motion: MotionDef;
  readonly emitter: EmitterDef;
  /** Nombre de ramassables lâchés à la mort. */
  readonly drops: number;
  readonly onUpdate?: EnemyUpdateHook;

  /** Parties destructibles apparues avec le porteur. */
  readonly parts?: readonly PartDef[];

  /** Marqueur EXPLICITE de boss. Le moteur le reconnaît (au lieu de déduire d'un
   *  préfixe de vague) : mort signalée par `Ev.BossKilled`, phases pilotées par
   *  `phaseSystem`. Absent/false = ennemi ordinaire. */
  readonly boss?: boolean;
  /** Phases de combat, déclenchées par SEUIL DE PV décroissant (voir `PhaseDef`).
   *  Absent = comportement unique (tous les ennemis sauf les boss phasés). */
  readonly phases?: readonly PhaseDef[];

  /** Ponte / éclosion : cet ennemi en engendre d'autres en cours de combat.
   *  Absent = il n'engendre rien (le cas de l'écrasante majorité). */
  readonly brood?: BroodDef | undefined;
  /** Si vrai, le porteur est INTOUCHABLE tant qu'une partie survit.
   *
   *  C'est ce qui transforme un sac à points de vie en combat structuré : le
   *  joueur choisit l'ordre de destruction, donc sa propre difficulté. Détruire
   *  les canons d'abord donne un combat long mais sûr ; s'attaquer au noyau
   *  n'est simplement pas permis tant qu'ils tirent. */
  readonly shieldedByParts?: boolean;

  /* ── Bouclier DIRECTIONNEL (PLAN.md §6.4, E08) ──────────────────────────
     À ne pas confondre avec `shieldedByParts` juste au-dessus : ce sont deux
     mécanismes sans rapport.

       shieldedByParts  → « tu ne peux pas toucher le noyau » (condition)
       shieldHp/Cone    → « tu ne peux pas le toucher DE CE CÔTÉ-LÀ » (géométrie)

     Le premier impose un ORDRE de destruction, le second impose un ANGLE
     d'attaque. Le premier se résout en réfléchissant, le second en se
     déplaçant — et c'est pour ça que le plan appelle le second un « révélateur
     de build » : il ne demande pas plus de dégâts, il demande une autre forme
     de gerbe. Une arme mono-cible doit contourner ; une arme large couvre les
     flancs sans y penser. */

  /** Points de vie du bouclier frontal. Absent = aucun bouclier.
   *
   *  Ils s'ajoutent aux `hp` du corps et suivent les mêmes coefficients (monde
   *  et balance) : un bouclier exempté du coefficient de monde tomberait à la
   *  même vitesse au monde 3 qu'au monde 1, et la structure du combat
   *  s'effondrerait au moment précis où elle devrait compter le plus. */
  readonly shieldHp?: number | undefined;
  /** DEMI-ouverture du cône protégé, en degrés (55 = un cône de ±55°). Le cône
   *  regarde le joueur — voir `collisionSystem` pour la convention d'axe. */
  readonly shieldConeDeg?: number | undefined;
}

export interface WaveSpawn {
  /** Instant dans la brique, en secondes. */
  readonly at: number;
  readonly enemy: string;
  /** Couloir 0..6. Les couloirs 0 et 6 sont des pièges de bord : interdits aux
   *  ennemis de plus de 200 PV (vérifié en CI). */
  readonly lane: number;
  readonly count: number;
  /** Écart entre deux ennemis d'un même groupe, en secondes. */
  readonly spacing: number;
}

export interface WaveDef {
  readonly id: string;
  readonly duration: number;
  readonly spawns: readonly WaveSpawn[];
}

/** Une SENTINELLE montée sur un obstacle porteur — le cœur du « complexe
 *  militaire ».
 *
 *  Elle référence un `EnemyDef` ordinaire, comme `PartDef` : une sentinelle est
 *  un ennemi comme un autre, qui se trouve apparaître À la position d'un
 *  obstacle plutôt que dans un couloir. Rien de spécifique n'est nécessaire côté
 *  moteur — on réutilise un ennemi ANCRÉ (`Motion.Anchor`, le Mortier), qui se
 *  poste et se lit comme du terrain. C'est ce qui rend le système peu coûteux :
 *  aucune capacité neuve, seulement un point d'apparition différent. */
export interface SentinelMount {
  /** Identifiant de l'ennemi ancré posté sur le porteur. */
  readonly enemy: string;
  /** Décalage du point d'apparition par rapport au centre de l'obstacle, en
   *  unités. */
  readonly offX: number;
  readonly offY: number;
  /** Si vrai, la sentinelle CHEVAUCHE l'obstacle : elle le SUIT où qu'il aille et
   *  part avec lui à sa sortie. Indispensable sur une plateforme MOBILE (sinon la
   *  sentinelle se détacherait aussitôt). Absent/false = comportement historique :
   *  la sentinelle naît à la position de l'obstacle puis vit sa propre vie (une
   *  tourelle ancrée indépendante), et l'obstacle défile sous elle. */
  readonly ride?: boolean | undefined;
}

/** Le placement DÉCLARATIF d'un obstacle dans un bloc de niveau.
 *
 *  Le pendant, côté décor collisionnable, d'un `WaveSpawn` côté ennemis : là où
 *  une vague dit « tel ennemi, tel couloir, tel instant », un placement dit
 *  « tel obstacle, telle abscisse, telle hauteur d'entrée ». La différence tient
 *  à la nature de l'objet — un obstacle ne pilote pas de trajectoire, il suit le
 *  défilement global : sa seule liberté est OÙ (l'abscisse `x`) et QUAND (l'
 *  ordonnée de départ, encodée par `at`).
 *
 *  ⚠️ `at` n'est PAS une échéance programmée. `placeObstacles` est appelé UNE
 *  fois, à l'activation du bloc, et pose tous les obstacles d'un coup — comme la
 *  vitrine. `at` est converti en ordonnée de départ AU-DESSUS du cadre
 *  (`PF_HH + OBSTACLE_SCROLL_SPEED × at`) : l'obstacle franchit alors le haut du
 *  cadre `at` secondes après l'activation, en descendant au défilement. Un `at`
 *  croissant échelonne donc la section dans le temps sans qu'aucun minuteur ne
 *  soit nécessaire — la descente EST le minuteur.
 *
 *  ⚠️ Abscisse ABSOLUE, pas un couloir. Les sept couloirs sont une grille pensée
 *  pour la nuée d'ennemis ; un champ d'astéroïdes ne s'y aligne pas, et l'y
 *  contraindre ferait des rochers en colonnes régulières — l'exact contraire d'un
 *  champ. */
export interface ObstaclePlacement {
  /** Secondes après l'activation du bloc où l'obstacle franchit le haut du cadre
   *  (≥ 0). Converti en ordonnée de départ par l'appelant. */
  readonly at: number;
  /** Identifiant de l'obstacle porteur (astéroïde, ou pièce de complexe). */
  readonly obstacle: string;
  /** Abscisse absolue du centre, en unités monde. */
  readonly x: number;
  /** Sentinelles montées dessus. Présentes = c'est un COMPLEXE MILITAIRE (un
   *  porteur armé) ; absentes = un simple obstacle de décor. */
  readonly sentinels?: readonly SentinelMount[];
}

export interface LevelBlock {
  /** Instant dans le niveau, en secondes. */
  readonly at: number;
  readonly wave: string;
  /** Obstacles posés à l'activation de ce bloc (astéroïdes, complexes
   *  militaires). Absent = le bloc ne pose aucun décor collisionnable, le cas de
   *  l'écrasante majorité. Lu par `placeObstacles`, appelé depuis `levelSystem`.
   *
   *  ⚠️ C'est une donnée INERTE tant que le hook de `levelSystem` n'est pas
   *  branché : un bloc porteur d'`obstacles` mais dérouleé par un moteur qui les
   *  ignore se comporte exactement comme avant. C'est ce qui rend l'ajout
   *  rigoureusement transparent pour le golden replay (qui tourne sur `testIndex`,
   *  sans aucun bloc à obstacles). */
  readonly obstacles?: readonly ObstaclePlacement[];
}

export interface LevelDef {
  readonly id: string;
  readonly duration: number;
  readonly blocks: readonly LevelBlock[];
  /** Coefficient de points de vie du monde (PLAN.md §6.4) : W1 ×1,0, W2 ×1,6,
   *  W3 ×2,4. Absent vaut 1.
   *
   *  ⚠️ Il porte sur le NIVEAU et non sur une constante globale, parce que
   *  c'est une propriété du monde traversé et non de la partie. Le mettre dans
   *  `balance.json` obligerait à le faire varier en cours de run — c'est-à-dire
   *  à donner un état mutable à ce qui doit rester une table de lecture.
   *
   *  ⚠️ Il est COMPENSÉ par la courbe de puissance du build. Ce n'est pas un
   *  mur : c'est ce qui maintient la tension pendant que le joueur devient
   *  nettement plus fort. Un ratio plat produirait la sensation de grind. */
  readonly hpScale?: number;
}

/* ═══════════════════════════════════════════════════════════════════════════
   OBSTACLES — le décor COLLISIONNABLE (astéroïdes ; plus tard, les complexes
   militaires à sentinelles).

   Une définition d'obstacle décrit une FORME que la simulation sait faire
   descendre, bloquer et briser — au même titre qu'un `EnemyDef` décrit une forme
   qu'elle sait faire vivre. Elle vit ici, dans src/sim/, pour la même raison :
   la simulation exécute des formes, le contenu fournit des données, et la flèche
   de dépendance ne s'inverse jamais (voir l'en-tête de ce fichier).

   ⚠️ Il n'y a délibérément ni `motion` ni `emitter` : un obstacle ne pilote pas
   de trajectoire (il suit le défilement global, voir `obstacleSystem`) et ne
   tire pas. Le jour où l'on voudrait un rocher qui dérive latéralement, ce sera
   une capacité MOTEUR nouvelle — un champ ajouté ici et lu par `obstacleSystem`,
   pas un hook glissé dans le contenu. C'est la même frontière que pour `Motion`.
   ═══════════════════════════════════════════════════════════════════════════ */
/** Mouvement PROPRE d'un obstacle, EN PLUS du défilement vers le bas — ce qui
 *  fait d'un simple rocher une PLATEFORME VOLANTE. Absent = l'obstacle ne fait que
 *  descendre (le cas courant). Déterministe : dérive constante + ondulation
 *  sinusoïdale, calculées depuis l'âge, sans le moindre RNG. */
export interface ObstacleDriftDef {
  /** Dérive latérale CONSTANTE, en u/s (signe = sens). Une plateforme qui traverse
   *  l'écran en biais pendant qu'elle descend. */
  readonly driftX?: number | undefined;
  /** Amplitude de l'ONDULATION latérale, en unités. Une plateforme qui ondule. */
  readonly swayAmp?: number | undefined;
  /** Période de l'ondulation, en secondes. Ignorée si `swayAmp` absent. */
  readonly swayPeriod?: number | undefined;
}

export interface ObstacleDef {
  readonly id: string;
  /** Rayon de collision ET rayon visuel, en unités. Un seul nombre : le contrat
   *  d'assistance (hurtbox à 120 %) est réservé aux ennemis, un obstacle n'a rien
   *  à cacher ni à offrir. Ce que le joueur voit est exactement ce qui bloque. */
  readonly radius: number;
  /** Mouvement propre (plateforme volante). Absent = descend seulement. */
  readonly drift?: ObstacleDriftDef | undefined;
  /** Marqueur VISUEL : rendu comme une DALLE plate (plateforme mécanique) au lieu
   *  d'un rocher culbutant. Purement présentation — la simulation reste un disque
   *  de rayon `radius`. Absent = rocher (le cas courant). */
  readonly platform?: boolean | undefined;
  /** Points de vie. Ignoré si `destructible` est faux — un indestructible ne se
   *  brise jamais, quelle que soit la valeur posée ici. */
  readonly hp: number;
  /** Faux = INDESTRUCTIBLE : PV infinis, on ne peut que l'esquiver. C'est une
   *  vraie source de difficulté — un gros rocher qu'aucune arme n'entame oblige
   *  au déplacement, là où un destructible n'est qu'un mur de PV. Absent = vrai
   *  (le cas courant, un petit astéroïde qu'on fait sauter). */
  readonly destructible?: boolean;
  /** Points marqués à la destruction. Absent = zéro : détruire un obstacle est
   *  d'abord un dégagement de terrain, pas une source de score. */
  readonly score?: number;
}

/* ═══════════════════════════════════════════════════════════════════════════
   VAISSEAUX et ARMES PRIMAIRES.

   Un vaisseau et une arme sont, tous les deux, un PROFIL DE STATISTIQUES : une
   liste de `Modifier` semée dans `progress.baseMods` au démarrage de la run,
   exactement comme les bonus de méta-progression. Trois conséquences, et ce
   sont elles qui justifient de ne pas avoir écrit un système à part :

     · le profil emprunte le chemin des cartes ramassées — additifs d'abord,
       multiplicatifs ensuite, plafonds à la fin. Un chemin séparé finirait par
       diverger, et deux joueurs au même build n'auraient plus les mêmes
       statistiques ;
     · ajouter un vaisseau ou une arme reste un FICHIER, sans une ligne de
       moteur ;
     · l'ordre dans lequel on empile profil de vaisseau, profil d'arme et bonus
       de méta n'a aucune importance, ce qui supprime toute une famille de bugs
       de câblage.

   ⚠️ Ce que le vocabulaire des statistiques ne sait PAS exprimer.
   `StatBlock` décrit dix grandeurs et rien d'autre. « Recharge des secondaires
   deux fois plus rapide » et « bombe rechargeable au frôlement » sont des
   MÉCANIQUES, pas des chiffres de ce vocabulaire. Elles sont donc déclarées
   ici en clair (`secondaryCooldownMul`, `grazeBombCharge`) et lues par le
   câblage, jamais par `computeStats`. Les inventer sous forme de statistique
   aurait été pire : une entrée de plus dans `StatBlock` que le moteur
   n'applique nulle part, c'est-à-dire exactement le défaut de `maxBombs`.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Un VAISSEAU : la coque pilotée pendant la run.
 *
 *  ⚠️ Un vaisseau est un ÉCHANGE, jamais une amélioration (PLAN.md §6.9). Un
 *  profil strictement meilleur que celui de série rendrait la coque de départ
 *  obsolète et supprimerait le choix ; un profil strictement pire ferait payer
 *  le joueur pour se punir. `validateShips` refuse les deux. */
export interface ShipDef {
  readonly id: string;
  readonly name: string;
  /** Le profil, dans le vocabulaire des cartes. Vide = la coque de série. */
  readonly modifiers: readonly Modifier[];
  /** Facteur appliqué à la recharge des armes secondaires. 1 = inchangé.
   *
   *  ⚠️ Lu par le CÂBLAGE, pas par `computeStats` : il n'existe pas de
   *  statistique de recharge, et en fabriquer une aurait obligé la simulation à
   *  connaître les armes secondaires depuis `StatBlock`. */
  readonly secondaryCooldownMul: number;
  /** Frôlements nécessaires pour regagner une bombe. 0 = aucune recharge.
   *
   *  ⚠️ Même statut que ci-dessus : une promesse déclarée, consommée par le
   *  système de frôlement. Déclarée quand même — une promesse écrite dans le
   *  texte d'un nœud et absente du code est invisible au premier qui cherche. */
  readonly grazeBombCharge: number;
  /** Texte du hangar. Chiffré, même règle que les cartes et les secondaires :
   *  deux coques doivent pouvoir se comparer sans les essayer. */
  readonly text: string;
}

/** Une ARME PRIMAIRE : le tir automatique, toujours actif (PLAN.md §6.3).
 *
 *  ⚠️ Les armes se distinguent par la FORME du tir, jamais par la puissance.
 *  Leur DPS nominal reste comparable à ±`WEAPON_DPS_TOLERANCE` — une arme qui
 *  ferait 30 % de plus ne serait pas un choix, ce serait la bonne réponse, et
 *  les deux autres deviendraient du contenu mort. */
export interface WeaponDef {
  readonly id: string;
  readonly name: string;
  readonly modifiers: readonly Modifier[];
  readonly text: string;
}

/** Coque et arme dont dispose TOUT joueur, y compris sans un seul achat.
 *
 *  ⚠️ Les deux valent `vulcan`, et ce n'est pas une étourderie : le plan (§6.3)
 *  nomme VULCAN l'arme de départ, et le texte du nœud AEGIS nomme VULCAN la
 *  coque de départ. Les deux catalogues sont DISJOINTS (`ships` et `weapons`,
 *  deux tableaux, deux tables d'index) et l'identifiant n'a de sens que dans le
 *  sien. Le jour où l'on serait tenté de les fusionner en une seule table
 *  d'identifiants, c'est cette collision qui le rendrait impossible — et tant
 *  mieux : rien ne relie une coque à un canon.
 *
 *  Résolus par IDENTIFIANT et jamais par index 0, pour la même raison que la
 *  secondaire par défaut : l'index dépend de l'ordre alphabétique des chemins
 *  ramassés par le glob, donc renommer un fichier changerait l'équipement de
 *  départ sans que personne ne le remarque. */
export const DEFAULT_SHIP_ID = 'vulcan';
export const DEFAULT_WEAPON_ID = 'vulcan';

/** Écart de DPS nominal toléré entre deux armes primaires. */
export const WEAPON_DPS_TOLERANCE = 0.1;

/** Statistiques d'un profil seul, sans aucune carte ni bonus de méta. C'est la
 *  grandeur sur laquelle raisonnent l'équilibrage et les assertions. */
export function profileStats(mods: readonly Modifier[]): Record<StatId, number> {
  return computeStats(mods);
}

/**
 * Espacement vertical entre deux volées d'une arme, en unités.
 *
 * `vitesse ÷ cadence` — la distance que parcourt un projectile avant que le
 * suivant ne parte. C'est LA grandeur qui distingue les trois armes autrement
 * que par le DPS : plus elle est courte, plus la colonne est dense, et plus une
 * file d'ennemis alignés se fait traverser. La perforation du jeu est
 * entièrement géométrique (voir `SecShape.Lance`) — un projectile meurt sur sa
 * première cible, c'est le suivant qui poursuit.
 */
export function columnSpacing(s: Record<StatId, number>): number {
  return s.bulletSpeed / s.fireRate;
}

/* ── Assertions de conception ─────────────────────────────────────────────
   Même statut que `validateSecondaries` et `checkFeelInvariants` : ce ne sont
   pas des tests de code, ce sont des garde-fous contre une dérive de design.
   Elles font échouer le BUILD le jour où quelqu'un remonte « juste un peu » les
   dégâts d'une arme pour la rendre satisfaisante. ── */

function idProblems(ids: readonly string[], famille: string, defaut: string): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    if (seen.has(id)) problems.push(`${famille} dupliqué : ${id}`);
    seen.add(id);
  }
  if (ids.length > 0 && !seen.has(defaut)) {
    // Sans le contenu de série, un joueur sans achat n'a rien à piloter : la
    // run démarrerait sur un profil vide ou sur le premier fichier du glob.
    problems.push(`${famille} : le contenu de série « ${defaut} » est absent du catalogue`);
  }
  return problems;
}

/** Une statistique poussée AU PLAFOND par le seul profil perd en silence tout
 *  ce que la run ajoutera ensuite. Le profil doit rester strictement en
 *  dessous ; c'est la run qui a le droit de saturer, pas le point de départ. */
function capProblems(s: Record<StatId, number>, quoi: string): string[] {
  const problems: string[] = [];
  for (const k of Object.keys(STAT_CAPS) as StatId[]) {
    const cap = STAT_CAPS[k];
    if (cap !== undefined && s[k] >= cap) {
      problems.push(`${quoi} : ${k} = ${s[k]} atteint le plafond ${cap} dès le profil seul`);
    }
  }
  return problems;
}

export function validateShips(list: readonly ShipDef[]): string[] {
  const problems: string[] = [...idProblems(list.map((s) => s.id), 'vaisseau', DEFAULT_SHIP_ID)];

  for (const d of list) {
    if (d.secondaryCooldownMul <= 0) {
      problems.push(`${d.id} : secondaryCooldownMul doit être > 0`);
    }
    if (d.grazeBombCharge < 0) problems.push(`${d.id} : grazeBombCharge doit être ≥ 0`);

    if (d.id === DEFAULT_SHIP_ID) {
      /* ⚠️ La coque de série n'a PAS de profil, et c'est une contrainte dure.
         Le banc d'équilibrage (`tests/balance/curve.test.ts`) mesure la courbe
         de puissance sur elle : lui donner le moindre modificateur déplacerait
         la référence de tout l'équilibrage du jeu sans qu'aucun test ne dise
         pourquoi. */
      if (d.modifiers.length > 0) {
        problems.push(`${d.id} : la coque de série doit avoir un profil VIDE`);
      }
      if (d.secondaryCooldownMul !== 1 || d.grazeBombCharge !== 0) {
        problems.push(`${d.id} : la coque de série ne promet rien hors statistiques`);
      }
      continue;
    }

    const s = profileStats(d.modifiers);
    problems.push(...capProblems(s, d.id));

    let renoncement = false;
    let contrepartie = false;
    for (const k of Object.keys(BASE_STATS) as StatId[]) {
      if (s[k] < BASE_STATS[k]) renoncement = true;
      if (s[k] > BASE_STATS[k]) contrepartie = true;
    }
    // Une promesse hors statistiques compte comme contrepartie : c'est tout
    // l'intérêt de l'avoir déclarée. FANAL échange ses bombes contre une
    // recharge de secondaire, et rien de cela n'apparaît dans `StatBlock`.
    if (d.secondaryCooldownMul < 1 || d.grazeBombCharge > 0) contrepartie = true;

    if (!renoncement) {
      problems.push(
        `${d.id} : aucun renoncement — un vaisseau strictement meilleur que la ` +
          `coque de série la rend obsolète et supprime le choix (PLAN.md §6.9)`,
      );
    }
    if (!contrepartie) {
      problems.push(
        `${d.id} : aucune contrepartie — un vaisseau strictement pire fait payer ` +
          `le joueur pour se punir`,
      );
    }
  }

  return problems;
}

export function validateWeapons(list: readonly WeaponDef[]): string[] {
  const problems: string[] = [...idProblems(list.map((w) => w.id), 'arme', DEFAULT_WEAPON_ID)];

  const reference = dps(BASE_STATS);
  for (const d of list) {
    if (d.id === DEFAULT_WEAPON_ID) {
      // Même raison que pour la coque de série : le VULCAN EST la référence des
      // 200 DPS sur laquelle tout le bestiaire est calibré.
      if (d.modifiers.length > 0) {
        problems.push(`${d.id} : l'arme de série doit avoir un profil VIDE`);
      }
      continue;
    }

    const s = profileStats(d.modifiers);
    problems.push(...capProblems(s, d.id));

    if (s.fireRate <= 0) problems.push(`${d.id} : cadence nulle`);
    if (s.damage <= 0) problems.push(`${d.id} : dégâts nuls`);
    if (s.bulletCount < 1) problems.push(`${d.id} : aucun projectile par volée`);

    /* ⚠️ LA règle du système, l'exact pendant de celle des secondaires. Les
       armes se distinguent par la forme du tir — cadence contre dégâts, densité
       de colonne contre portée — jamais par la puissance. Une arme à +30 % de
       DPS n'est pas un choix, c'est la bonne réponse. */
    const ecart = (dps(s) - reference) / reference;
    const abs = ecart < 0 ? -ecart : ecart;
    if (abs > WEAPON_DPS_TOLERANCE) {
      problems.push(
        `${d.id} : ${Math.round(dps(s))} DPS nominal contre ${Math.round(reference)} pour ` +
          `l'arme de série (écart ${Math.round(ecart * 100)} %, max ` +
          `±${Math.round(WEAPON_DPS_TOLERANCE * 100)} %)`,
      );
    }
  }

  return problems;
}

/** Multiplicateurs globaux, rechargés à chaud en développement.
 *  Le seul endroit du contenu qui reste du JSON — parce que c'est le seul qui
 *  doit changer SANS recompiler (PLAN.md §3.3). */
export interface Balance {
  readonly enemyHp: number;
  readonly enemyFireRate: number;
  readonly bulletSpeed: number;
  readonly spawnDensity: number;
}

export const DEFAULT_BALANCE: Balance = {
  enemyHp: 1,
  enemyFireRate: 1,
  bulletSpeed: 1,
  spawnDensity: 1,
};

/**
 * L'index que la simulation reçoit. Construit par l'appelant à partir du
 * registre de contenu — ou à la main dans un test, ce qui permet d'éprouver un
 * comportement sans embarquer tout le catalogue.
 */
/** Une entrée du roster du mode SANS FIN.
 *
 *  Le mode sans fin n'a pas de `LevelDef` : ce roster EST son contenu. Chaque
 *  entrée dit quel ennemi peut apparaître, à partir de quel PALIER temporel
 *  (`tier` — le mode débloque un palier de plus toutes les N secondes) et avec
 *  quel poids relatif dans le tirage. La montée en difficulté vient de trois
 *  choses combinées : des paliers plus hauts (donc des ennemis plus lourds
 *  éligibles), des PV qui grimpent, et des impulsions plus rapprochées. */
export interface EndlessEntry {
  readonly enemy: string;
  /** Palier à partir duquel l'ennemi devient éligible (0 = dès le début). */
  readonly tier: number;
  /** Poids relatif dans le tirage pondéré parmi les éligibles. */
  readonly weight: number;
}

export interface DefIndex extends SecondaryIndex {
  /** Définitions d'ennemis, indexées par leur identifiant numérique. */
  readonly enemies: readonly EnemyDef[];
  /** id textuel → index numérique. Les pools stockent l'entier ; les fichiers
   *  de contenu se réfèrent au texte. */
  readonly enemyByI: ReadonlyMap<string, number>;
  readonly waves: ReadonlyMap<string, WaveDef>;
  readonly levels: ReadonlyMap<string, LevelDef>;
  /** Définitions d'obstacles, indexées par leur identifiant numérique. Vide sur
   *  un index de test qui n'éprouve pas les obstacles — comme pour les ennemis,
   *  la simulation ne connaît que la forme, jamais les fichiers. */
  readonly obstacles: readonly ObstacleDef[];
  /** id textuel → index numérique. Le pool stocke l'entier (son `type`) ; les
   *  fichiers de contenu se réfèrent au texte. */
  readonly obstacleByI: ReadonlyMap<string, number>;
  /** Pool d'améliorations proposées à chaque montée de niveau. */
  readonly mods: readonly ModDef[];
  /** Coques pilotables, dans l'ordre du catalogue. */
  readonly ships: readonly ShipDef[];
  /** id textuel → index numérique, même rôle que `secondaryByI`. */
  readonly shipByI: ReadonlyMap<string, number>;
  /** Armes primaires, dans l'ordre du catalogue. */
  readonly weapons: readonly WeaponDef[];
  readonly weaponByI: ReadonlyMap<string, number>;
  /* ⚠️ Les définitions de méta-progression transitent par l'index de contenu,
     mais leurs TYPES vivent dans src/game/ : elles ne sont jamais lues par la
     simulation, seulement par la couche application entre deux runs. Les
     placer ici serait plus commode et brouillerait la frontière. */
  readonly metaNodes: readonly import('../game/meta').MetaNodeDef[];
  readonly metaMilestones: readonly import('../game/meta').MilestoneDef[];
  /** Roster du mode sans fin. Vide sur un index de test qui n'éprouve pas ce
   *  mode — la simulation ne connaît que la forme, jamais les fichiers. */
  readonly endlessPool: readonly EndlessEntry[];
  balance: Balance;
}

export const EMPTY_INDEX: DefIndex = {
  enemies: [],
  enemyByI: new Map(),
  waves: new Map(),
  levels: new Map(),
  obstacles: [],
  obstacleByI: new Map(),
  mods: [],
  ships: [],
  shipByI: new Map(),
  weapons: [],
  weaponByI: new Map(),
  secondaries: [],
  secondaryByI: new Map(),
  metaNodes: [],
  metaMilestones: [],
  endlessPool: [],
  balance: DEFAULT_BALANCE,
};
